import { DataRefreshEvent } from "@fp/admin-api-client";
import {
  type BatchProcessItemDto,
  BatchProcessItemStatus,
  listPayoutRequests,
  type PayoutRequestListItemDto,
  PayoutRequestStatus,
} from "@fp/admin-api-client";
import { getBatchProcessById, listBatchProcessItems } from "@fp/admin-api-client/batch-processes/batch-processes.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { cx } from "@fp/ui/utils/cx";
import { Button } from "@fp/ui/components/base/buttons/button";
import { AddressMapPopover } from "@fp/ui/components/common/address-map-popover";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { useApprovePayoutRequest, useBulkApprovePayoutRequests } from "@fp/admin-api-client/payout-requests/payout-requests.client";
import { useGetApiAdminFundingBalance } from "@fp/admin-api-client/funding/funding.client";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { AlertTriangle, ChevronRight, File06, User01 } from "@untitledui/icons";
import { getErrorMessage } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { useCallback, useMemo, useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { HighlightMatches } from "@/components/highlight-matches";
import { getPayoutMemoTexts, PayoutMemoLines } from "@/components/payout-memos";
import { getSourceLabel, getSourceLabelLines, getSourceTypeKey, payoutRequestSourceTypeOptions, SourceTag } from "@/components/payout-source";
import { computePayFacetOptions, matchesPayRangeFilters, PayListFilters } from "@/components/pay-list-filters";
import { BulkApproveModal } from "@/components/bulk-approve-modal";
import { BulkApproveResultsModal } from "@/components/bulk-approve-results-modal";
import { SelectionControl } from "@/components/selection-control";
import { useBatchProcesses } from "@/components/batch-process/batch-process-context";
import { getRunningPayoutApproval } from "@/components/batch-process/batch-process-status";
import { AdminPageHeader } from "@/components/admin-page-header";
import { FsmAllocationModal } from "@/components/fsm-allocation-modal";
import { InvoiceExportDialog } from "@/components/invoice-export-dialog";
import {
  type BulkApproveRow,
  bulkApproveExclusionBanners,
  countBulkApproveExclusions,
  describeBulkApproveSelection,
  getBulkApproveExclusionReason,
  getBulkApproveGroupSelection,
  isBulkApproveRowSelectable,
} from "@/lib/bulk-approve-selection";
import { invoiceStatusLabels } from "@/lib/invoice-utils";
import {
  LIST_SHORTCUTS,
  PayoutShortcutsHelp,
} from "@/components/payout-shortcuts-help";
import { PayoutBalanceWarningModal } from "@/components/payout-balance-warning-modal";
import { useDataRefresh } from "@/hooks/useSignalR";
import { usePayListFilters } from "@/hooks/usePayListFilters";
import { type PayCategory, PayCategoryLabels } from "@/lib/pay-categories";
import { getPayoutRequestRoute } from "@/lib/payout-request-route";
import {
  type PayoutReviewQueueEntry,
  writePayoutReviewQueue,
} from "@/lib/payout-review-queue";

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const statusFilterOptions = [
  PayoutRequestStatus.Pending,
  PayoutRequestStatus.Approved,
  PayoutRequestStatus.Returned,
  PayoutRequestStatus.Denied,
].map((status) => ({ id: status, label: status }));

const getStatusesForCategory = (
  category: PayCategory,
): PayoutRequestStatus[] | undefined => {
  switch (category) {
    case "all":
      return undefined;
    case "review":
      return [PayoutRequestStatus.Pending];
    case "returned":
      return [PayoutRequestStatus.Returned];
    case "approved":
      return [PayoutRequestStatus.Approved];
    case "denied":
      return [PayoutRequestStatus.Denied];
    default:
      return undefined;
  }
};

export const Route = createFileRoute("/payout-requests/categories/$category")({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params }) => {
    const category = params.category as PayCategory;
    const statuses = getStatusesForCategory(category);
    const payoutRequests = await queryClient.fetchQuery({
      queryKey: ["payout-requests", "category", category, "latestPerClaim"],
      queryFn: () =>
        listPayoutRequests({
          page: 1,
          pageSize: 1000,
          latestPerClaim: true,
          statuses,
        }),
    });
    const items = payoutRequests?.data?.items ?? [];
    return {
      category,
      items,
    };
  },
  pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
  const { category, items } = Route.useLoaderData();
  const router = useRouter();
  const [isInvoiceExportOpen, setIsInvoiceExportOpen] = useState(false);

  const invalidatePayoutRequests = useCallback(() => {
    router.invalidate();
  }, [router]);
  useDataRefresh(DataRefreshEvent.PayoutRequestApproved, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.BatchProcessItemCompleted, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.BatchProcessCompleted, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestDenied, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestReturned, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestSubmitted, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestResubmitted, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestEdited, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestGenerated, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestAllocated, invalidatePayoutRequests);
  useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidatePayoutRequests);

  return (
    <AdminLayout>
      <AdminPageHeader
        title={PayCategoryLabels[category as PayCategory]}
        parent={{ url: "/payout-requests", label: "Pay" }}
        titleActions={
          <>
            <Button size="sm" color="secondary" iconLeading={File06} onClick={() => setIsInvoiceExportOpen(true)}>
              Export invoices
            </Button>
            <PayoutShortcutsHelp shortcuts={LIST_SHORTCUTS} />
          </>
        }
      />
      <PageContentContainer className="p-0">
        <PayoutRequestList
          key={category}
          category={category as PayCategory}
          items={items}
        />
      </PageContentContainer>
      <InvoiceExportDialog isOpen={isInvoiceExportOpen} onOpenChange={setIsInvoiceExportOpen} />
    </AdminLayout>
  );
}

type PayoutRequestRowSelection = {
  isSelected: boolean;
  isSelectable: boolean;
  reason?: string | null;
  onToggle: () => void;
};

type PayoutRequestListItemProps = {
  item: PayoutRequestListItemDto;
  highlightQuery: string;
  onOpen: () => void;
  hideAmount?: boolean;
  hideSourceLabel?: boolean;
  selection?: PayoutRequestRowSelection;
};

const PayoutRequestListItem = ({
  item,
  highlightQuery,
  onOpen,
  hideAmount = false,
  hideSourceLabel = false,
  selection,
}: PayoutRequestListItemProps) => {
  const { payoutRequest } = item;
  const sourceLines = hideSourceLabel ? [] : getSourceLabelLines(item.source);
  const hasMemo = item.payout ? getPayoutMemoTexts(item.payout).length > 0 : false;
  const truncateWhenNotSearching = highlightQuery.trim() === "" && "truncate";
  const sourceAddress = hideSourceLabel ? undefined : item.source.address;
  const isUnselectable = selection !== undefined && !selection.isSelectable;

  return (
    <div
      className={cx(
        "flex flex-row items-center w-full",
        "border-b border-gray-200",
        isUnselectable ? "bg-gray-50" : "bg-white hover:bg-gray-50 transition-colors",
      )}
    >
      {selection && (
        <SelectionControl
          isSelectable={selection.isSelectable}
          isSelected={selection.isSelected}
          label={`Select ${payoutRequest.payeeName}`}
          unavailableLabel={`${payoutRequest.payeeName} can't be approved yet`}
          onToggle={selection.onToggle}
          className="pl-4"
        />
      )}
      <button
        type="button"
        onClick={selection ? (selection.isSelectable ? selection.onToggle : undefined) : onOpen}
        className="flex flex-col flex-1 min-w-0 text-left px-4 py-3"
      >
        <div className="flex flex-row items-start justify-between w-full mb-1">
          <div className="flex items-center gap-2 flex-1 min-w-0 pr-4">
            <User01 className="h-4 w-4 text-gray-400 shrink-0" />
            <h3 className={cx("text-base font-semibold text-brand-600", truncateWhenNotSearching)}>
              <HighlightMatches text={payoutRequest.payeeName} query={highlightQuery} />
            </h3>
          </div>
          <div className="text-base font-semibold text-brand-600 whitespace-nowrap">
            {hideAmount || payoutRequest.amount == null ? (
              ""
            ) : (
              <HighlightMatches text={formatCurrency(payoutRequest.amount)} query={highlightQuery} />
            )}
          </div>
        </div>

        {(sourceLines.length > 0 || hasMemo) && (
          <div className="mb-1 pr-4">
            {sourceLines.map((line) => (
              <div key={line} className={cx("text-sm text-gray-700", truncateWhenNotSearching)}>
                <HighlightMatches text={line} query={highlightQuery} />
              </div>
            ))}
            {item.payout && <PayoutMemoLines payout={item.payout} highlightQuery={highlightQuery} className={cx(truncateWhenNotSearching)} />}
          </div>
        )}

        <div className="flex flex-row items-center justify-between w-full text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <SourceTag source={item.source} />
            <PayoutRequestStatusBadge status={payoutRequest.status} />
            {payoutRequest.invoice && (
              <span className="text-xs text-gray-500">Invoice {payoutRequest.invoice.number} · {invoiceStatusLabels[payoutRequest.invoice.status]}</span>
            )}
          </div>
          <div className="text-xs">{formatLocalDate(payoutRequest.createdDate, "dateWithYear")}</div>
        </div>

        {isUnselectable && selection?.reason && (
          <div className="mt-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="size-4 shrink-0 mt-0.5 text-amber-600" />
            <span>{selection.reason}</span>
          </div>
        )}
      </button>
      {sourceAddress && <AddressMapPopover address={sourceAddress} />}
      {!selection && (
        <button type="button" onClick={onOpen} tabIndex={-1} aria-hidden="true" className="self-stretch flex items-center shrink-0 pl-3 pr-4">
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </button>
      )}
    </div>
  );
};

type PayoutRequestStatusBadgeProps = {
  status: string;
};

const PayoutRequestStatusBadge = ({
  status,
}: PayoutRequestStatusBadgeProps) => {
  const getStatusStyles = (status: string) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Approved":
        return "bg-blue-100 text-blue-800";
      case "Returned":
        return "bg-orange-100 text-orange-800";
      case "Denied":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
        getStatusStyles(status),
      )}
    >
      {status}
    </span>
  );
};

type PayoutRequestListProps = {
  category: PayCategory;
  items: PayoutRequestListItemDto[];
};

type MultiTechInvoiceGroupProps = {
  items: PayoutRequestListItemDto[];
  highlightQuery: string;
  needsAllocation: boolean;
  onAllocate: () => void;
  onOpenItem: (item: PayoutRequestListItemDto) => void;
  getRowSelection?: (item: PayoutRequestListItemDto) => PayoutRequestRowSelection;
  onToggleGroup?: () => void;
};

const MultiTechInvoiceGroup = ({
  items,
  highlightQuery,
  needsAllocation,
  onAllocate,
  onOpenItem,
  getRowSelection,
  onToggleGroup,
}: MultiTechInvoiceGroupProps) => {
  const router = useRouter();
  const approveMutation = useApprovePayoutRequest();
  const { data: balanceData } = useGetApiAdminFundingBalance();
  const [isBalanceWarningOpen, setIsBalanceWarningOpen] = useState(false);

  const availableBalance = balanceData?.data?.availableBalance ?? 0;
  const pendingDeposits = balanceData?.data?.pendingDeposits ?? 0;
  const groupTotal = items.reduce(
    (sum, item) => sum + (item.payoutRequest.amount ?? 0),
    0,
  );
  const allHaveAmount = items.every(
    (item) => item.payoutRequest.amount != null,
  );
  const hasInsufficientBalance = availableBalance < groupTotal;

  const payGroup = async () => {
    const outcomes = await Promise.allSettled(
      items.map((item) =>
        approveMutation.mutateAsync({
          id: item.payoutRequest.id,
          data: { notes: undefined, concurrencyToken: item.payoutRequest.concurrencyToken },
        }),
      ),
    );

    let paidCount = 0;
    outcomes.forEach((outcome, index) => {
      if (outcome.status === "rejected") {
        toast.error(
          `${items[index].payoutRequest.payeeName} can't be paid yet`,
          { description: getErrorMessage(outcome.reason, "Failed to pay") },
        );
      } else {
        paidCount += 1;
      }
    });

    if (paidCount > 0) {
      toast.success(
        paidCount === 1
          ? "Paid 1 technician"
          : `Paid ${paidCount} technicians`,
      );
    }

    router.invalidate();
  };

  const sourceLabel = getSourceLabel(items[0].source);
  const fsmAddress = items[0].source.address;
  const rowSelections = getRowSelection ? items.map((item) => getRowSelection(item)) : null;
  const groupSelection = rowSelections ? getBulkApproveGroupSelection(rowSelections) : null;

  return (
    <div className="mx-4 my-3 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-100 px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {groupSelection && (
            <SelectionControl
              isSelectable={groupSelection.isSelectable}
              isSelected={groupSelection.isSelected}
              isIndeterminate={groupSelection.isIndeterminate}
              label="Toggle all technicians in this invoice"
              unavailableLabel="No technician in this invoice can be approved yet"
              onToggle={onToggleGroup}
            />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-700">
              Multi-tech invoice · {items.length} technicians
            </div>
            {sourceLabel && (
              <div className={cx("text-xs text-gray-500", highlightQuery.trim() === "" && "truncate")}>
                <HighlightMatches text={sourceLabel} query={highlightQuery} />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {fsmAddress && <AddressMapPopover address={fsmAddress} />}
          {!rowSelections &&
            (needsAllocation ? (
              <Button color="primary" size="sm" onClick={onAllocate}>
                Allocate
              </Button>
            ) : (
              <Button
                color="primary"
                size="sm"
                isDisabled={approveMutation.isPending || !allHaveAmount}
                onClick={
                  hasInsufficientBalance
                    ? () => setIsBalanceWarningOpen(true)
                    : payGroup
                }
              >
                {approveMutation.isPending ? "Paying..." : "Pay"}
              </Button>
            ))}
        </div>
      </div>
      {items.map((item) => (
        <PayoutRequestListItem
          key={item.payoutRequest.id}
          item={item}
          highlightQuery={highlightQuery}
          hideAmount={needsAllocation}
          hideSourceLabel
          onOpen={() => onOpenItem(item)}
          selection={rowSelections ? getRowSelection?.(item) : undefined}
        />
      ))}
      <PayoutBalanceWarningModal
        isOpen={isBalanceWarningOpen}
        onOpenChange={setIsBalanceWarningOpen}
        amount={groupTotal}
        availableBalance={availableBalance}
        pendingDeposits={pendingDeposits}
      />
    </div>
  );
};

type GroupedInvoice = {
  fsmCompletedWorkId: string;
  allocationConfirmed: boolean;
  items: PayoutRequestListItemDto[];
};

const PayoutRequestList = ({ category, items }: PayoutRequestListProps) => {
  const navigate = useNavigate();
  const router = useRouter();
  const [allocationFsmCompletedWorkId, setAllocationFsmCompletedWorkId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkApproveOpen, setIsBulkApproveOpen] = useState(false);
  const [isBulkBalanceWarningOpen, setIsBulkBalanceWarningOpen] = useState(false);
  const [activeBatchProcessId, setActiveBatchProcessId] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<{
    succeededCount: number;
    alreadyApprovedCount: number;
    failedCount: number;
    alreadyApprovedItems: BatchProcessItemDto[];
    failedItems: BatchProcessItemDto[];
  } | null>(null);
  const bulkApproveMutation = useBulkApprovePayoutRequests();
  const { batches } = useBatchProcesses();
  const runningApproval = getRunningPayoutApproval(batches);
  const { data: balanceData } = useGetApiAdminFundingBalance();
  const { data: capabilitiesData } = useGetOrganizationContextCapabilities();
  const canBulkApprove = capabilitiesData?.data?.canBulkApprovePayoutRequests ?? false;
  const {
    selectedSourceTypes,
    selectedStatuses,
    selectedPayeeNames,
    selectedSourceLabels,
    amountMin,
    amountMax,
    submittedFrom,
    submittedTo,
    setSelectedSourceTypes,
    setSelectedStatuses,
    setSelectedPayeeNames,
    setSelectedSourceLabels,
    setAmountMin,
    setAmountMax,
    setSubmittedFrom,
    setSubmittedTo,
    clearFilters,
  } = usePayListFilters("payoutRequestFilters");

  const { sourceTypeOptions, payeeNames, sourceLabels } = useMemo(() => {
    const facetOptions = computePayFacetOptions(
      items.map((item) => ({
        sourceTypeKey: getSourceTypeKey(item.source) ?? null,
        status: category === "all" ? item.payoutRequest.status : null,
        payeeName: item.payoutRequest.payeeName,
        sourceLabel: getSourceLabel(item.source) ?? null,
        amount: item.payoutRequest.amount,
        createdDate: item.payoutRequest.createdDate,
      })),
      {
        sourceTypes: selectedSourceTypes,
        statuses: selectedStatuses,
        payeeNames: selectedPayeeNames,
        sourceLabels: selectedSourceLabels,
        amountMin,
        amountMax,
        submittedFrom,
        submittedTo,
      },
    );

    return {
      sourceTypeOptions: payoutRequestSourceTypeOptions.filter(
        (option) => selectedSourceTypes.has(option.id) || facetOptions.sourceTypeKeys.has(option.id),
      ),
      payeeNames: facetOptions.payeeNames,
      sourceLabels: facetOptions.sourceLabels,
    };
  }, [items, category, selectedSourceTypes, selectedStatuses, selectedPayeeNames, selectedSourceLabels, amountMin, amountMax, submittedFrom, submittedTo]);

  const { multiTechGroups, singleItems } = useMemo(() => {
    const groups = new Map<string, GroupedInvoice>();
    const ungrouped: PayoutRequestListItemDto[] = [];

    for (const item of items) {
      const grouping = item.fsmGrouping;
      if (grouping) {
        const existing = groups.get(grouping.fsmCompletedWorkId);
        if (existing) {
          existing.items.push(item);
        } else {
          groups.set(grouping.fsmCompletedWorkId, {
            fsmCompletedWorkId: grouping.fsmCompletedWorkId,
            allocationConfirmed: grouping.allocationConfirmed,
            items: [item],
          });
        }
      } else {
        ungrouped.push(item);
      }
    }

    const query = searchQuery.trim().toLowerCase();
    const matchesFilters = (item: PayoutRequestListItemDto) => {
      const { payoutRequest, source } = item;
      if (
        selectedSourceTypes.size > 0 &&
        !selectedSourceTypes.has(getSourceTypeKey(source) ?? "")
      ) {
        return false;
      }
      if (
        category === "all" &&
        selectedStatuses.size > 0 &&
        !selectedStatuses.has(payoutRequest.status)
      ) {
        return false;
      }
      if (
        selectedPayeeNames.size > 0 &&
        !selectedPayeeNames.has(payoutRequest.payeeName)
      ) {
        return false;
      }
      if (
        selectedSourceLabels.size > 0 &&
        !selectedSourceLabels.has(getSourceLabel(source) ?? "")
      ) {
        return false;
      }
      if (
        !matchesPayRangeFilters(payoutRequest.amount, payoutRequest.createdDate, {
          amountMin,
          amountMax,
          submittedFrom,
          submittedTo,
        })
      ) {
        return false;
      }
      if (query === "") {
        return true;
      }
      const searchTargets = [
        payoutRequest.payeeName,
        ...getSourceLabelLines(source),
        ...(item.payout ? getPayoutMemoTexts(item.payout) : []),
      ];
      if (payoutRequest.amount != null) {
        searchTargets.push(
          formatCurrency(payoutRequest.amount),
          payoutRequest.amount.toFixed(2),
        );
      }
      return searchTargets.some((target) => target.toLowerCase().includes(query));
    };

    const multiTechGroups = [...groups.values()]
      .filter((group) => group.items.length > 1)
      .filter((group) => group.items.some(matchesFilters));
    const singleItems = [
      ...ungrouped,
      ...[...groups.values()]
        .filter((group) => group.items.length === 1)
        .flatMap((group) => group.items),
    ]
      .filter(matchesFilters)
      .sort(
        (a, b) =>
          new Date(b.payoutRequest.createdDate).getTime() -
          new Date(a.payoutRequest.createdDate).getTime(),
      );

    return { multiTechGroups, singleItems };
  }, [items, category, searchQuery, selectedSourceTypes, selectedStatuses, selectedPayeeNames, selectedSourceLabels, amountMin, amountMax, submittedFrom, submittedTo]);

  const { bulkApprovableItems, bulkPendingCount, bulkExclusions } = useMemo(() => {
    if (category !== "review") {
      return { bulkApprovableItems: [], bulkPendingCount: 0, bulkExclusions: [] };
    }
    const pendingRows: BulkApproveRow[] = [
      ...multiTechGroups.flatMap((group) =>
        group.items.map((item) => ({ item, allocationConfirmed: group.allocationConfirmed })),
      ),
      ...singleItems.map((item) => ({ item, allocationConfirmed: true })),
    ].filter((row) => row.item.payoutRequest.status === PayoutRequestStatus.Pending);
    return {
      bulkApprovableItems: pendingRows.filter(isBulkApproveRowSelectable).map((row) => row.item),
      bulkPendingCount: pendingRows.length,
      bulkExclusions: countBulkApproveExclusions(pendingRows),
    };
  }, [category, multiTechGroups, singleItems]);

  const bulkApproveTotal = bulkApprovableItems.reduce(
    (sum, item) => sum + (item.payoutRequest.amount ?? 0),
    0,
  );
  const selectedItems = useMemo(
    () => bulkApprovableItems.filter((item) => selectedIds.has(item.payoutRequest.id)),
    [bulkApprovableItems, selectedIds],
  );
  const selectedTotal = selectedItems.reduce(
    (sum, item) => sum + (item.payoutRequest.amount ?? 0),
    0,
  );
  const availableBalance = balanceData?.data?.availableBalance ?? 0;
  const pendingDeposits = balanceData?.data?.pendingDeposits ?? 0;

  const enterBulkApprove = () => {
    setSelectedIds(new Set(bulkApprovableItems.map((item) => item.payoutRequest.id)));
    setIsSelecting(true);
  };

  const exitBulkApprove = () => {
    setIsSelecting(false);
    setSelectedIds(new Set());
  };

  const toggleSelected = (payoutRequestId: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(payoutRequestId)) {
        next.delete(payoutRequestId);
      } else {
        next.add(payoutRequestId);
      }
      return next;
    });
  };

  const getRowSelection = (item: PayoutRequestListItemDto, allocationConfirmed = true): PayoutRequestRowSelection => {
    const row = { item, allocationConfirmed };
    const exclusionReason = getBulkApproveExclusionReason(row);

    return {
      isSelected: selectedIds.has(item.payoutRequest.id),
      isSelectable: isBulkApproveRowSelectable(row),
      reason: exclusionReason === null ? null : bulkApproveExclusionBanners[exclusionReason],
      onToggle: () => toggleSelected(item.payoutRequest.id),
    };
  };

  const toggleGroupSelected = (group: GroupedInvoice) => {
    const selectableIds = group.items
      .filter((item) => isBulkApproveRowSelectable({ item, allocationConfirmed: group.allocationConfirmed }))
      .map((item) => item.payoutRequest.id);
    if (selectableIds.length === 0) {
      return;
    }
    setSelectedIds((previous) => {
      const next = new Set(previous);
      const allSelected = selectableIds.every((id) => next.has(id));
      for (const id of selectableIds) {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });
  };

  const filterCriteria = useMemo(() => {
    const parts: string[] = [];
    const query = searchQuery.trim();
    if (query !== "") {
      parts.push(`Search: "${query}"`);
    }
    if (selectedSourceTypes.size > 0) {
      const labels = payoutRequestSourceTypeOptions
        .filter((option) => selectedSourceTypes.has(option.id))
        .map((option) => option.label);
      parts.push(`Source: ${labels.join(", ")}`);
    }
    if (selectedPayeeNames.size > 0) {
      parts.push(`Payee: ${[...selectedPayeeNames].join(", ")}`);
    }
    if (selectedSourceLabels.size > 0) {
      parts.push(`Opportunity/job: ${[...selectedSourceLabels].join(", ")}`);
    }
    if (amountMin !== "" && amountMax !== "") {
      parts.push(`Amount: ${amountMin} to ${amountMax}`);
    } else if (amountMin !== "") {
      parts.push(`Amount: at least ${amountMin}`);
    } else if (amountMax !== "") {
      parts.push(`Amount: at most ${amountMax}`);
    }
    if (submittedFrom !== "" && submittedTo !== "") {
      parts.push(`Submitted: ${submittedFrom} to ${submittedTo}`);
    } else if (submittedFrom !== "") {
      parts.push(`Submitted: on or after ${submittedFrom}`);
    } else if (submittedTo !== "") {
      parts.push(`Submitted: on or before ${submittedTo}`);
    }
    if (parts.length === 0) {
      return null;
    }
    const description = parts.join("; ");
    return description.length > 1000 ? `${description.slice(0, 997)}...` : description;
  }, [searchQuery, selectedSourceTypes, selectedPayeeNames, selectedSourceLabels, amountMin, amountMax, submittedFrom, submittedTo]);

  const bulkApprove = async () => {
    try {
      const response = await bulkApproveMutation.mutateAsync({
        data: {
          payoutRequests: selectedItems.map((item) => ({
            payoutRequestId: item.payoutRequest.id,
            concurrencyToken: item.payoutRequest.concurrencyToken,
          })),
          filterCriteria,
        },
      });

      setActiveBatchProcessId(response.data.batchProcessId);
      setIsBulkApproveOpen(false);
      exitBulkApprove();
    } catch (error) {
      setIsBulkApproveOpen(false);
      exitBulkApprove();
      router.invalidate();
      toast.error("Bulk approval rejected", {
        description: getErrorMessage(error, "Failed to approve payout requests"),
      });
    }
  };

  const handleBatchCompleted = async () => {
    const batchProcessId = activeBatchProcessId;
    if (!batchProcessId) {
      return;
    }
    try {
      const batchResponse = await getBatchProcessById(batchProcessId);
      const batch = batchResponse.data;
      if (!batch.isComplete) {
        return;
      }
      setActiveBatchProcessId(null);
      if (batch.failedCount === 0 && batch.noActionCount === 0) {
        return;
      }
      const listSettledItems = (status: BatchProcessItemStatus) =>
        listBatchProcessItems(batchProcessId, { pageSize: 100, statuses: [status] });
      const [failedResponse, alreadyApprovedResponse] = await Promise.all([
        batch.failedCount > 0 ? listSettledItems(BatchProcessItemStatus.Failed) : null,
        batch.noActionCount > 0 ? listSettledItems(BatchProcessItemStatus.NoAction) : null,
      ]);
      setBatchResults({
        succeededCount: batch.succeededCount,
        alreadyApprovedCount: batch.noActionCount,
        failedCount: batch.failedCount,
        alreadyApprovedItems: alreadyApprovedResponse?.data.items ?? [],
        failedItems: failedResponse?.data.items ?? [],
      });
    } catch (error) {
      setActiveBatchProcessId(null);
      toast.error("Couldn't load bulk approval results", {
        description: getErrorMessage(error, "Check the payout request list for current statuses"),
      });
    }
  };
  useDataRefresh(DataRefreshEvent.BatchProcessCompleted, handleBatchCompleted);

  const confirmBulkApprove = () => {
    if (availableBalance < selectedTotal) {
      setIsBulkBalanceWarningOpen(true);
      return;
    }
    bulkApprove();
  };

  const payoutReviewQueue = useMemo<PayoutReviewQueueEntry[]>(
    () =>
      singleItems
        .filter(
          (item) => item.payoutRequest.status === PayoutRequestStatus.Pending,
        )
        .map((item) => ({
          payoutRequestId: item.payoutRequest.id,
          workflow: item.source.workflow,
          opportunityClaimId: item.source.opportunityClaim?.opportunityClaimId,
        })),
    [singleItems],
  );

  const openPayoutRequest = useCallback(
    (item: PayoutRequestListItemDto) => {
      writePayoutReviewQueue(payoutReviewQueue);
      const returnState = {
        returnTo: `/payout-requests/categories/${category}`,
        returnLabel: PayCategoryLabels[category],
      };
      navigate({
        ...getPayoutRequestRoute(item.payoutRequest.id, item.source),
        state: returnState,
      });
    },
    [navigate, category, payoutReviewQueue],
  );

  if (items.length === 0) {
    return (
      <div className="bg-white text-center py-8 text-gray-500">
        No payout requests found
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white">
      <PayListFilters
        searchPlaceholder="Search payout requests"
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        sourceTypeOptions={sourceTypeOptions}
        selectedSourceTypes={selectedSourceTypes}
        onSelectedSourceTypesChange={setSelectedSourceTypes}
        statusOptions={statusFilterOptions}
        showStatusFilters={category === "all"}
        selectedStatuses={selectedStatuses}
        onSelectedStatusesChange={setSelectedStatuses}
        payeeNames={payeeNames}
        selectedPayeeNames={selectedPayeeNames}
        onSelectedPayeeNamesChange={setSelectedPayeeNames}
        sourceLabels={sourceLabels}
        selectedSourceLabels={selectedSourceLabels}
        onSelectedSourceLabelsChange={setSelectedSourceLabels}
        amountMin={amountMin}
        onAmountMinChange={setAmountMin}
        amountMax={amountMax}
        onAmountMaxChange={setAmountMax}
        submittedFrom={submittedFrom}
        onSubmittedFromChange={setSubmittedFrom}
        submittedTo={submittedTo}
        onSubmittedToChange={setSubmittedTo}
        onClearFilters={clearFilters}
      />
      {canBulkApprove && !isSelecting && bulkApprovableItems.length > 0 && (
        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
          <div className="text-sm text-gray-700">
            {bulkApprovableItems.length}
            {bulkPendingCount > bulkApprovableItems.length && ` of ${bulkPendingCount}`}{" "}
            {bulkPendingCount === 1 ? "request" : "requests"} ready to approve ·{" "}
            {formatCurrency(bulkApproveTotal)}
          </div>
          <Button
            color="primary"
            size="sm"
            isDisabled={runningApproval !== null}
            onClick={enterBulkApprove}
          >
            {runningApproval === null ? "Bulk approve" : "Approval in progress"}
          </Button>
        </div>
      )}
      {isSelecting && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <div className="flex flex-col gap-1 text-sm text-gray-700">
            <span>{describeBulkApproveSelection(selectedItems.length, bulkPendingCount, bulkExclusions)}</span>
            <span>
              {formatCurrency(selectedTotal)} · Available Balance{" "}
              <span
                className={
                  availableBalance < selectedTotal
                    ? "font-medium text-red-600"
                    : "font-medium text-gray-900"
                }
              >
                {formatCurrency(availableBalance)}
              </span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                color="link-color"
                size="sm"
                isDisabled={selectedItems.length === bulkApprovableItems.length}
                onClick={() => setSelectedIds(new Set(bulkApprovableItems.map((item) => item.payoutRequest.id)))}
              >
                Select all
              </Button>
              <span className="text-gray-400">·</span>
              <Button
                color="link-color"
                size="sm"
                isDisabled={selectedItems.length === 0}
                onClick={() => setSelectedIds(new Set())}
              >
                Deselect all
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button color="secondary" size="sm" onClick={exitBulkApprove}>
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              isDisabled={selectedItems.length === 0}
              onClick={() => setIsBulkApproveOpen(true)}
            >
              Approve selected ({selectedItems.length})
            </Button>
          </div>
        </div>
      )}
      {multiTechGroups.length === 0 && singleItems.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No payout requests match the current filters
        </div>
      )}
      {multiTechGroups.map((group) => (
        <MultiTechInvoiceGroup
          key={group.fsmCompletedWorkId}
          items={group.items}
          highlightQuery={searchQuery}
          needsAllocation={!group.allocationConfirmed}
          onAllocate={() => setAllocationFsmCompletedWorkId(group.fsmCompletedWorkId)}
          onOpenItem={openPayoutRequest}
          getRowSelection={isSelecting ? (item) => getRowSelection(item, group.allocationConfirmed) : undefined}
          onToggleGroup={isSelecting ? () => toggleGroupSelected(group) : undefined}
        />
      ))}
      {singleItems.map((item) => (
        <PayoutRequestListItem
          key={item.payoutRequest.id}
          item={item}
          highlightQuery={searchQuery}
          onOpen={() => openPayoutRequest(item)}
          selection={isSelecting ? getRowSelection(item) : undefined}
        />
      ))}
      {allocationFsmCompletedWorkId && (
        <FsmAllocationModal
          fsmCompletedWorkId={allocationFsmCompletedWorkId}
          isOpen={allocationFsmCompletedWorkId !== null}
          onClose={() => setAllocationFsmCompletedWorkId(null)}
          onConfirmed={() => router.invalidate()}
        />
      )}
      <BulkApproveModal
        isOpen={isBulkApproveOpen}
        onOpenChange={setIsBulkApproveOpen}
        items={selectedItems}
        filterCriteria={filterCriteria}
        isSubmitting={bulkApproveMutation.isPending}
        availableBalance={availableBalance}
        onConfirm={confirmBulkApprove}
      />
      <PayoutBalanceWarningModal
        isOpen={isBulkBalanceWarningOpen}
        onOpenChange={setIsBulkBalanceWarningOpen}
        amount={selectedTotal}
        availableBalance={availableBalance}
        pendingDeposits={pendingDeposits}
      />
      {batchResults && (
        <BulkApproveResultsModal
          isOpen={batchResults !== null}
          onOpenChange={(open) => {
            if (!open) {
              setBatchResults(null);
            }
          }}
          succeededCount={batchResults.succeededCount}
          alreadyApprovedCount={batchResults.alreadyApprovedCount}
          failedCount={batchResults.failedCount}
          alreadyApprovedItems={batchResults.alreadyApprovedItems}
          failedItems={batchResults.failedItems}
        />
      )}
    </div>
  );
};
